import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation } from "@apollo/client/react";
import { OutreachQueueDocument, ScheduleClientFollowUpDocument } from "@/graphql/clients.generated";
import type { ScheduledFollowUp } from "@/pages/Alumni/components/ScheduleFollowUpDialog";
import { CONTACT_TYPE_FOR_CHANNEL, formatDay, toCalendarDate } from "@/pages/Alumni/helpers/client";

/**
 * Save a follow-up a coordinator scheduled from a client's profile. No contact is logged, so the
 * contact history is untouched.
 *
 * The mutation answers with the client's `nextFollowUpDate`, which Apollo writes into the cached
 * client, so the alumni list's "Next follow-up" column shows it without a refetch. The queue is
 * re-read: the client now sits wherever the follow-up's date puts them.
 *
 * Rejects when the save fails, after saying so.
 */
export const useScheduleFollowUp = () => {
    const { t } = useTranslation();
    const [scheduleClientFollowUp] = useMutation(ScheduleClientFollowUpDocument);

    /** `clientName` is for the confirmation. */
    return async (clientId: string, followUp: ScheduledFollowUp, clientName: string): Promise<void> => {
        try {
            await scheduleClientFollowUp({
                variables: {
                    clientId,
                    input: {
                        followUpDate: toCalendarDate(followUp.date),
                        type: CONTACT_TYPE_FOR_CHANNEL[followUp.channel],
                        notes: followUp.note ?? null,
                    },
                },
                refetchQueries: [OutreachQueueDocument],
                awaitRefetchQueries: true,
            });
        } catch (error) {
            toast.error(t("admin.alumni.followUp.failed", { name: clientName }));
            throw error;
        }
        toast.success(t("admin.alumni.followUp.scheduled", { name: clientName, date: formatDay(followUp.date) }));
    };
};
